import {
    deleteCompareInChart,
    deleteLibraryDocument,
    deleteLibraryFolder,
    deleteScope,
    ILibraryListItem,
} from "@/api";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { formatItemSummary } from "../utils/formatItemSummary";

interface BulkDeleteDialogProps {
    open: boolean;
    onClose: () => void;
    items: ILibraryListItem[];
    onDeleted: () => void;
}

const deleteItem = (item: ILibraryListItem) => {
    switch (item.type) {
        case "document":
            return deleteLibraryDocument(item.id);
        case "scope":
            return deleteScope(item.id);
        case "comparison":
            return deleteCompareInChart(item.id);
        case "folder":
            return deleteLibraryFolder(item.id);
    }
};

export const BulkDeleteDialog = ({
    open,
    onClose,
    items,
    onDeleted,
}: BulkDeleteDialogProps) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const hasFolders = items.some((i) => i.type === "folder");
    const summary = formatItemSummary(items);
    const title =
        items.length === 1
            ? `Delete ${items[0].type.charAt(0).toUpperCase()}${items[0].type.slice(1)}`
            : `Delete ${items.length} items`;
    const warningCopy = hasFolders
        ? "This action cannot be undone. Folders and all their contents will be permanently removed."
        : "This action cannot be undone. Selected items will be permanently removed.";

    const handleSubmit = async () => {
        setIsDeleting(true);
        setError(null);

        // Cascade-avoiding order: non-folders first, then folders. Folder
        // cascade can race with item-level deletes if both fire in parallel.
        const nonFolders = items.filter((i) => i.type !== "folder");
        const folders = items.filter((i) => i.type === "folder");
        const nonFolderResults = await Promise.allSettled(
            nonFolders.map(deleteItem)
        );
        const folderResults = await Promise.allSettled(folders.map(deleteItem));
        const results = [...nonFolderResults, ...folderResults];
        const failed = results.filter((r) => r.status === "rejected").length;
        const succeeded = results.length - failed;

        setIsDeleting(false);

        if (failed === 0) {
            toast.success(
                `Deleted ${succeeded} item${succeeded !== 1 ? "s" : ""}`
            );
            onClose();
            onDeleted();
        } else if (succeeded > 0) {
            toast.warning(
                `Deleted ${succeeded} item${succeeded !== 1 ? "s" : ""}, ${failed} failed`
            );
            onClose();
            onDeleted();
        } else {
            setError(
                `Failed to delete ${failed} item${failed !== 1 ? "s" : ""}`
            );
        }
    };

    const handleClose = () => {
        if (isDeleting) return;
        setError(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                if (!v && !isDeleting) handleClose();
            }}
        >
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    {/*
                     * Override data-slot to match Unit 5.RED's alert-dialog
                     * lookup. shadcn AlertDialog isn't installed; rendering a
                     * Dialog with the AlertDialog slot keeps the test contract
                     * stable without a new dep.
                     */}
                    <DialogTitle data-slot="alert-dialog-title">
                        {title}
                    </DialogTitle>
                </DialogHeader>
                <div
                    role="alert"
                    data-slot="alert"
                    className="border-destructive/15 bg-destructive/5 flex items-start gap-3 rounded-md border p-3 text-sm"
                >
                    <AlertTriangle className="text-destructive size-4 shrink-0" />
                    <p className="text-foreground">{warningCopy}</p>
                </div>
                <div
                    data-slot="alert-dialog-description"
                    className="space-y-2 text-sm"
                >
                    {items.length === 1 ? (
                        <>
                            <p>
                                Are you sure you want to delete this{" "}
                                {items[0].type}?
                            </p>
                            <p className="break-all">
                                <strong>{items[0].name}</strong>
                            </p>
                        </>
                    ) : (
                        <p>Are you sure you want to delete {summary}?</p>
                    )}
                </div>
                {error && <p className="text-destructive text-sm">{error}</p>}
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isDeleting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleSubmit}
                        disabled={isDeleting}
                        aria-label="Delete"
                    >
                        {isDeleting ? (
                            <>
                                <Loader2 className="mr-2 size-4 animate-spin" />
                                Deleting…
                            </>
                        ) : (
                            "Delete"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
